const INSIGHTS_STORAGE_KEY_PREFIX = 'hrv4.insights';
const INSIGHTS_TIMESTAMP_KEY_PREFIX = 'hrv4.insights.timestamp';
const INSIGHTS_STATUS_KEY_PREFIX = 'hrv4.insights.status';
const INSIGHTS_EVENT_NAME = 'hrv4:insights-updated';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION_MS = 5 * 60 * 1000;

function normalizeUserId(value) {
  const id = String(value || '').trim();
  if (!id || id === 'null' || id === 'undefined') return null;
  return id;
}

export function getCurrentUserId() {
  if (typeof window === 'undefined') return null;
  return (
    normalizeUserId(window.localStorage.getItem('userId')) ||
    normalizeUserId(window.localStorage.getItem('currentUserId')) ||
    normalizeUserId(window.sessionStorage.getItem('userId'))
  );
}

export function getInsightsStorageKey(userId = getCurrentUserId()) {
  const normalizedUserId = normalizeUserId(userId);
  if (!normalizedUserId) return null;
  return `${INSIGHTS_STORAGE_KEY_PREFIX}:${normalizedUserId}`;
}

export function getInsightsTimestampKey(userId = getCurrentUserId()) {
  const normalizedUserId = normalizeUserId(userId);
  if (!normalizedUserId) return null;
  return `${INSIGHTS_TIMESTAMP_KEY_PREFIX}:${normalizedUserId}`;
}

export function getInsightsStatusKey(userId = getCurrentUserId()) {
  const normalizedUserId = normalizeUserId(userId);
  if (!normalizedUserId) return null;
  return `${INSIGHTS_STATUS_KEY_PREFIX}:${normalizedUserId}`;
}

export function hasValidInsightsCache(userId = getCurrentUserId()) {
  if (typeof window === 'undefined') return false;

  const timestampKey = getInsightsTimestampKey(userId);
  const insightsKey = getInsightsStorageKey(userId);

  if (!timestampKey || !insightsKey) return false;

  try {
    const timestamp = window.localStorage.getItem(timestampKey);
    const insights = window.localStorage.getItem(insightsKey);

    if (!timestamp || !insights) return false;

    const cachedTime = parseInt(timestamp, 10);
    const now = Date.now();

    // Check if cache is still valid (within 5 minutes)
    if (now - cachedTime > CACHE_DURATION_MS) return false;

    return true;
  } catch {
    return false;
  }
}

export function loadStoredInsights(userId = getCurrentUserId()) {
  if (typeof window === 'undefined') return null;

  const insightsKey = getInsightsStorageKey(userId);
  if (!insightsKey) return null;

  try {
    const raw = window.localStorage.getItem(insightsKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function loadStoredModelStatus(userId = getCurrentUserId()) {
  if (typeof window === 'undefined') return null;

  const statusKey = getInsightsStatusKey(userId);
  if (!statusKey) return null;

  try {
    return window.localStorage.getItem(statusKey);
  } catch {
    return null;
  }
}

export function storeInsights(insights, modelStatus, userId = getCurrentUserId()) {
  if (typeof window === 'undefined') return false;

  const insightsKey = getInsightsStorageKey(userId);
  const timestampKey = getInsightsTimestampKey(userId);
  const statusKey = getInsightsStatusKey(userId);

  if (!insightsKey || !timestampKey || !statusKey) return false;

  try {
    window.localStorage.setItem(insightsKey, JSON.stringify(insights));
    window.localStorage.setItem(timestampKey, String(Date.now()));
    window.localStorage.setItem(statusKey, modelStatus);

    emitInsightsUpdate(userId);
    return true;
  } catch {
    return false;
  }
}

export function clearInsightsCache(userId = getCurrentUserId()) {
  if (typeof window === 'undefined') return;

  const insightsKey = getInsightsStorageKey(userId);
  const timestampKey = getInsightsTimestampKey(userId);
  const statusKey = getInsightsStatusKey(userId);

  try {
    if (insightsKey) window.localStorage.removeItem(insightsKey);
    if (timestampKey) window.localStorage.removeItem(timestampKey);
    if (statusKey) window.localStorage.removeItem(statusKey);
  } catch {
    // Ignore storage failures
  }
}

function emitInsightsUpdate(userId = getCurrentUserId()) {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(INSIGHTS_EVENT_NAME, {
      detail: {
        userId: normalizeUserId(userId),
      },
    }),
  );
}

export function subscribeToInsightsUpdates(callback) {
  if (typeof window === 'undefined') return () => {};

  window.addEventListener(INSIGHTS_EVENT_NAME, callback);
  return () => window.removeEventListener(INSIGHTS_EVENT_NAME, callback);
}

// Force refresh - clear cache and trigger refetch
export function invalidateInsightsCache(userId = getCurrentUserId()) {
  clearInsightsCache(userId);
  emitInsightsUpdate(userId);
}
